// Malarone (atovaquone/proguanil) is a supply PGD. Weight is required —
// the runner prompts for weight if not already captured. Four weight
// bands modelled as four brands. The adult band has no upper weight
// limit. Quantities are calculated from departure and return dates by
// the runner — the config does not specify quantities, only the timing
// rules the pharmacist uses to verify the quantity. requiresLariamCard:
// false.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.MALARONE,
  displayName: "Malarone\u00ae (atovaquone/proguanil)",
  serviceType: "supply",
  minimumAgeYears: 0,
  minimumWeightKg: 11,
  indicationSummary:
    "Malaria prophylaxis for travel to areas with atovaquone/proguanil-sensitive malaria, as determined by NaTHNaC assessment and current ACMP guidance. Patient must weigh at least 11 kg.",

  brands: [
    {
      brandId: "malarone-band-11-20",
      brandName: "Malarone\u00ae Paediatric (11\u201320 kg)",
      minimumAgeYears: 0,
      minimumWeightKg: 11,
      maximumWeightKg: 20,
      route: "supply",
      doseDescription: "1 paediatric tablet (62.5 mg atovaquone / 25 mg proguanil) once daily",
      administrationNotes:
        "Supply paediatric-strength tablets. Label: one tablet daily with food or a milky drink. Start 1\u20132 days before entering the malaria-endemic area. Continue throughout. Continue for 7 days after leaving.",
    },
    {
      brandId: "malarone-band-21-30",
      brandName: "Malarone\u00ae Paediatric (21\u201330 kg)",
      minimumAgeYears: 0,
      minimumWeightKg: 21,
      maximumWeightKg: 30,
      route: "supply",
      doseDescription: "2 paediatric tablets (62.5 mg atovaquone / 25 mg proguanil each) once daily",
      administrationNotes:
        "Supply paediatric-strength tablets. Label: two tablets daily with food or a milky drink. Start 1\u20132 days before entering the malaria-endemic area. Continue throughout. Continue for 7 days after leaving.",
    },
    {
      brandId: "malarone-band-31-40",
      brandName: "Malarone\u00ae Paediatric (31\u201340 kg)",
      minimumAgeYears: 0,
      minimumWeightKg: 31,
      maximumWeightKg: 40,
      route: "supply",
      doseDescription: "3 paediatric tablets (62.5 mg atovaquone / 25 mg proguanil each) once daily",
      administrationNotes:
        "Supply paediatric-strength tablets. Label: three tablets daily with food or a milky drink. Start 1\u20132 days before entering the malaria-endemic area. Continue throughout. Continue for 7 days after leaving.",
    },
    {
      brandId: "malarone-band-over-40",
      brandName: "Malarone\u00ae Adult (over 40 kg)",
      minimumAgeYears: 0,
      minimumWeightKg: 41,
      route: "supply",
      doseDescription: "1 adult tablet (250 mg atovaquone / 100 mg proguanil) once daily",
      administrationNotes:
        "Supply adult-strength tablets. Label: one tablet daily with food or a milky drink. Start 1\u20132 days before entering the malaria-endemic area. Continue throughout. Continue for 7 days after leaving.",
    },
  ],

  schedules: [
    {
      scheduleId: "malarone-daily",
      label: "Once daily \u2014 start 1\u20132 days before entering risk area; continue 7 days after leaving",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "First dose: 1\u20132 days before entering the malaria-endemic area",
        },
      ],
      completionNotes:
        "Continue once daily throughout the trip. Continue for 7 days after leaving the malaria-endemic area. Quantity: 1\u20132 days pre-travel + trip duration + 7 days post-travel. If vomiting occurs within 1 hour of a dose, repeat the dose. Take at the same time each day.",
    },
  ],

  capturesPriorDoseHistory: false,

  supplyGates: [
    "NaTHNaC assessment confirms the destination has malaria risk and atovaquone/proguanil is the appropriate chemoprophylactic agent for the resistance pattern.",
    "Patient weight has been confirmed and is at least 11 kg.",
    "Departure date and return date are confirmed and the quantity to supply has been calculated: 1\u20132 days pre-travel + full trip duration + 7 days post-travel.",
  ],

  exclusions: [
    {
      checkId: "malarone-ex-weight",
      question: "Does the patient weigh less than 11 kg?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Malarone\u00ae is not indicated for patients weighing less than 11 kg. Do not supply.",
      referralMessage:
        "Refer to the patient's GP or a specialist travel health clinic for advice on malaria prophylaxis for patients under 11 kg.",
    },
    {
      checkId: "malarone-ex-hypersensitivity",
      question: "Does the patient have known hypersensitivity to atovaquone, proguanil hydrochloride, or any excipient of Malarone\u00ae?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Malarone\u00ae is contraindicated in this patient. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on alternative malaria prophylaxis.",
    },
    {
      checkId: "malarone-ex-pregnancy",
      question: "Is the patient known or suspected to be pregnant?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Malarone\u00ae is contraindicated in pregnancy. Do not supply.",
      referralMessage: "Refer to the patient's GP or obstetrician for advice on malaria prophylaxis in pregnancy.",
    },
    {
      checkId: "malarone-ex-breastfeeding",
      question: "Is the patient breastfeeding?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Malarone\u00ae is contraindicated in breastfeeding. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on malaria prophylaxis during breastfeeding.",
    },
    {
      checkId: "malarone-ex-renal",
      question:
        "Does the patient have severe renal impairment (creatinine clearance less than 30 mL/min) or is the patient receiving renal dialysis?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Malarone\u00ae is contraindicated in patients with severe renal impairment (creatinine clearance less than 30 mL/min) or those on renal dialysis. Do not supply.",
      referralMessage: "Refer to the patient's GP or renal specialist for advice on malaria prophylaxis.",
    },
    {
      checkId: "malarone-ex-interacting-medicines",
      question:
        "Is the patient currently taking any of the following: rifampicin, rifabutin, metoclopramide, efavirenz, boosted protease inhibitors (e.g. lopinavir/ritonavir), or tetracycline?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "These medicines significantly reduce atovaquone plasma concentrations and co-administration with Malarone\u00ae is not recommended. Do not supply.",
      referralMessage:
        "Refer to the patient's GP for advice on alternative malaria prophylaxis compatible with their current medicines.",
    },
  ],

  cautions: [
    {
      checkId: "malarone-caut-warfarin",
      question: "Is the patient taking warfarin or another coumarin anticoagulant?",
      cautionMessage:
        "Proguanil may potentiate the anticoagulant effect of warfarin and other coumarin anticoagulants. Advise the patient to have their INR monitored more frequently during the course of Malarone\u00ae. Notify the patient's GP of the supply.",
    },
    {
      checkId: "malarone-caut-hepatic",
      question: "Does the patient have known hepatic impairment?",
      cautionMessage:
        "Mild to moderate hepatic impairment: no dose adjustment required. Severe hepatic impairment: use with caution; assess the individual risk-benefit before proceeding and consider referral to the patient's GP.",
    },
    {
      checkId: "malarone-caut-lactose",
      question:
        "Has the patient been diagnosed with rare hereditary galactose intolerance, Lapp lactase deficiency, or glucose-galactose malabsorption?",
      cautionMessage:
        "Some formulations of Malarone\u00ae contain lactose. Check the specific product labelling. If the product contains lactose and the patient has one of these rare hereditary conditions, consider an alternative or seek pharmacist advice.",
    },
    {
      checkId: "malarone-caut-vomiting",
      question: "Does the patient have a history of vomiting or gastrointestinal intolerance to oral medication?",
      cautionMessage:
        "Malarone\u00ae must be taken with food or a milky drink to maximise absorption. If vomiting occurs within 1 hour of a dose, the dose must be repeated. Advise the patient accordingly. If vomiting is persistent, seek medical advice \u2014 parenteral antimalarial treatment may be required.",
    },
    {
      checkId: "malarone-caut-hypnozoites",
      question: "Is the patient travelling to an area co-endemic for Plasmodium vivax or Plasmodium ovale?",
      cautionMessage:
        "Malarone\u00ae has no activity against dormant liver-stage hypnozoites of P. vivax or P. ovale. It will not prevent relapse of these species after return. Advise the patient that if they develop symptoms of malaria up to 1 year after return (particularly within 3 months), they must seek urgent medical attention and inform the clinician of their travel history.",
    },
  ],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: false,
  requiresGPNotification: true,
  standardObservationMinutes: 0,
  extendedObservationMinutes: 0,

  patientAdvicePoints: [
    "Take one dose every day at the same time, with food or a milky drink. Do not take on an empty stomach.",
    "Start taking Malarone\u00ae 1\u20132 days before entering the malaria-endemic area.",
    "Continue taking Malarone\u00ae every day throughout your trip and for 7 days after you leave the malaria-endemic area. Do not stop early.",
    "If you vomit within 1 hour of taking a dose, take another dose as soon as possible.",
    "Malarone\u00ae does not protect against all types of malaria. If you are visiting areas with P. vivax or P. ovale, you may still be at risk of a relapse after returning home.",
    "Continue to use mosquito-bite avoidance measures throughout your trip: apply insect repellent containing DEET to exposed skin, wear long-sleeved clothing and long trousers (especially from dusk to dawn), and sleep under a permethrin-treated bed net.",
    "If you develop a fever or flu-like illness at any time during your trip or within 1 year of returning (especially within the first 3 months), seek urgent medical attention immediately and tell the clinician you have been in a malaria-risk area. Malaria can be fatal if not treated promptly.",
    "No antimalarial gives 100% protection. Using Malarone\u00ae together with bite-avoidance measures gives you the best protection available.",
  ],
});
